import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  LayoutDashboard, Video, CheckSquare, AlertTriangle, BarChart3, Settings,
  ChevronLeft, ChevronRight, Bell, Search, Moon, Sun, LogOut, User, X, Check, Play, Pause,
  Lock, HelpCircle, Users
} from 'lucide-react';
import { DashboardHome } from './dashboard/DashboardHome';
import { MeetingsScreen } from './dashboard/MeetingsScreen';
import { TasksScreen } from './dashboard/TasksScreen';
import { RiskComplianceScreen } from './dashboard/RiskComplianceScreen';
import { AnalyticsScreen } from './dashboard/AnalyticsScreen';
import { SettingsScreen } from './dashboard/SettingsScreen';
import { LeadsScreen } from './dashboard/LeadsScreen';
import { OnboardingTour } from './dashboard/OnboardingTour';
import { Logo } from './Logo';
import { DataProvider, useData } from '../context/DataContext';
import { useAuth } from '../context/AuthContext';
import { getPermissions } from '../utils/permissions';

const navItems = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, permission: 'canViewDashboard' },
  { id: 'meetings', label: 'Meetings', icon: Video, permission: 'canViewMeetings' },
  { id: 'tasks', label: 'Tasks', icon: CheckSquare, permission: 'canViewTasks' },
  { id: 'risk', label: 'Risk & Compliance', icon: AlertTriangle, permission: 'canViewRisks' },
  { id: 'analytics', label: 'Analytics', icon: BarChart3, permission: 'canViewAnalytics' },
  { id: 'leads', label: 'Leads', icon: Users, permission: 'canManageLeads' },
  { id: 'settings', label: 'Settings', icon: Settings, permission: 'canViewSettings' },
];

const NotificationsDropdown = ({ isOpen, onClose }) => {
  const { notifications, markNotificationRead, markAllNotificationsRead } = useData();
  const unreadCount = notifications.filter(n => !n.read).length;

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="absolute top-full right-0 mt-2 w-80 rounded-2xl bg-[#0d1221] border border-white/10 shadow-2xl overflow-hidden z-50"
    >
      <div className="flex items-center justify-between p-4 border-b border-white/10">
        <h3 className="font-semibold text-white">Notifications</h3>
        {unreadCount > 0 && (
          <button 
            onClick={markAllNotificationsRead}
            className="text-xs text-blue-400 hover:text-blue-300"
          >
            Mark all read
          </button>
        )}
      </div>
      <div className="max-h-80 overflow-y-auto">
        {notifications.length === 0 ? (
          <div className="p-4 text-center text-gray-400 text-sm">
            No notifications
          </div>
        ) : (
          notifications.map((notification) => (
            <div
              key={notification.id}
              onClick={() => markNotificationRead(notification.id)}
              className={`p-4 border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors ${
                !notification.read ? 'bg-blue-500/5' : ''
              }`}
            >
              <div className="flex items-start gap-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  notification.type === 'task' ? 'bg-blue-500/20 text-blue-400' :
                  notification.type === 'risk' ? 'bg-red-500/20 text-red-400' :
                  'bg-green-500/20 text-green-400'
                }`}>
                  {notification.type === 'task' ? <CheckSquare className="w-4 h-4" /> :
                   notification.type === 'risk' ? <AlertTriangle className="w-4 h-4" /> :
                   <Video className="w-4 h-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white">{notification.message}</p>
                  <p className="text-xs text-gray-500 mt-1">{notification.time}</p>
                </div>
                {!notification.read && (
                  <div className="w-2 h-2 rounded-full bg-blue-400 flex-shrink-0"></div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </motion.div>
  );
};

const DashboardContent = () => {
  const [activeScreen, setActiveScreen] = useState('dashboard');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [darkMode, setDarkMode] = useState(true);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const { notifications, getStats, demoMode, toggleDemoMode, wsConnected } = useData();
  const { user, logout, isDemo } = useAuth();
  const perms = getPermissions(user?.role);
  
  const stats = getStats();
  const unreadNotifications = notifications.filter(n => !n.read).length;

  // Filter nav items based on permissions
  const visibleNavItems = navItems.filter(item => perms[item.permission]);

  // Guard: if user navigates to a restricted screen, redirect to dashboard
  const safeScreen = visibleNavItems.some(n => n.id === activeScreen) ? activeScreen : 'dashboard';

  const renderScreen = () => {
    switch (safeScreen) {
      case 'dashboard':
        return <DashboardHome onNavigate={(id) => { if (visibleNavItems.some(n => n.id === id)) setActiveScreen(id); }} />;
      case 'meetings':
        return <MeetingsScreen />;
      case 'tasks':
        return <TasksScreen />;
      case 'risk':
        return <RiskComplianceScreen />;
      case 'analytics':
        return perms.canViewAnalytics ? <AnalyticsScreen /> : <DashboardHome onNavigate={setActiveScreen} />;
      case 'leads':
        return perms.canManageLeads ? <LeadsScreen /> : <DashboardHome onNavigate={setActiveScreen} />;
      case 'settings':
        return perms.canViewSettings ? <SettingsScreen /> : <DashboardHome onNavigate={setActiveScreen} />;
      default:
        return <DashboardHome onNavigate={setActiveScreen} />;
    }
  };

  const roleBadgeColors = {
    admin: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    manager: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    member: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  };

  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''}`}>
      <div className="min-h-screen bg-[#0a0e1a] text-white flex">
        {/* Sidebar */}
        <motion.aside
          initial={false}
          animate={{ width: sidebarCollapsed ? 80 : 260 }}
          className="fixed left-0 top-0 h-screen bg-[#0d1221]/90 backdrop-blur-xl border-r border-white/5 z-50 flex flex-col"
        >
          {/* Logo */}
          <div className="p-4 border-b border-white/5 flex items-center justify-between">
            {!sidebarCollapsed && <Logo size="small" />}
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="p-2 rounded-lg hover:bg-white/5 transition-colors ml-auto"
            >
              {sidebarCollapsed ? (
                <ChevronRight className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronLeft className="w-5 h-5 text-gray-400" />
              )}
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
            {visibleNavItems.map((item) => {
              // Show badges for tasks and risks
              let badge = null;
              if (item.id === 'tasks' && stats.overdueTasks > 0) {
                badge = stats.overdueTasks;
              } else if (item.id === 'risk' && stats.highRisks > 0) {
                badge = stats.highRisks;
              }

              return (
                <button
                  key={item.id}
                  onClick={() => setActiveScreen(item.id)}
                  className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-all duration-200 group relative ${
                    safeScreen === item.id
                      ? 'bg-gradient-to-r from-blue-600/20 to-cyan-600/20 text-white border border-blue-500/30'
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                  }`}
                  data-testid={`nav-${item.id}`}
                >
                  <item.icon className={`w-5 h-5 flex-shrink-0 ${
                    safeScreen === item.id ? 'text-blue-400' : 'group-hover:text-blue-400'
                  }`} />
                  {!sidebarCollapsed && (
                    <>
                      <span className="text-sm font-medium truncate">{item.label}</span>
                      {badge && (
                        <span className="ml-auto px-2 py-0.5 rounded-full bg-red-500/20 text-red-400 text-xs font-medium">
                          {badge}
                        </span>
                      )}
                    </>
                  )}
                  {safeScreen === item.id && !sidebarCollapsed && !badge && (
                    <div className="ml-auto w-1.5 h-1.5 rounded-full bg-blue-400"></div>
                  )}
                </button>
              );
            })}
          </nav>

          {/* User Section */}
          <div className="p-3 border-t border-white/5">
            <div className={`flex items-center gap-3 p-3 rounded-xl bg-white/5 ${sidebarCollapsed ? 'justify-center' : ''}`}>
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center flex-shrink-0">
                <span className="text-xs font-bold text-white">{user?.name?.split(' ').map(n => n[0]).join('') || 'U'}</span>
              </div>
              {!sidebarCollapsed && (
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate" data-testid="sidebar-user-name">{user?.name || 'User'}</p>
                  <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold capitalize border ${roleBadgeColors[user?.role] || roleBadgeColors.member}`}
                    data-testid="sidebar-role-badge"
                  >
                    {user?.role || 'member'}
                  </span>
                </div>
              )}
              {!sidebarCollapsed && (
                <button
                  onClick={() => { logout(); window.history.pushState({}, '', '/login'); window.dispatchEvent(new PopStateEvent('popstate')); }}
                  className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
                  data-testid="logout-btn"
                  title="Sign out"
                >
                  <LogOut className="w-4 h-4 text-gray-400" />
                </button>
              )}
            </div>
          </div>
        </motion.aside>

        {/* Main Content */}
        <div 
          className="flex-1 flex flex-col transition-all duration-300"
          style={{ marginLeft: sidebarCollapsed ? 80 : 260 }}
        >
          {/* Top Header */}
          <header className="sticky top-0 z-40 bg-[#0a0e1a]/80 backdrop-blur-xl border-b border-white/5 flex flex-col">
            {/* Demo Mode Banner */}
            {isDemo && (
              <div className="flex items-center justify-between px-6 py-2 bg-amber-500/10 border-b border-amber-500/20" data-testid="demo-mode-banner">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                  <span className="text-sm font-medium text-amber-300">Demo Mode</span>
                  <span className="text-xs text-amber-400/70 hidden sm:inline">— Sample Data</span>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => window.__d3RestartTour?.()}
                    className="text-xs text-amber-300 hover:text-amber-100 font-medium transition-colors"
                    data-testid="demo-take-tour-btn"
                  >
                    Take Tour
                  </button>
                  <button
                    onClick={() => { logout(); window.history.pushState({}, '', '/'); window.dispatchEvent(new PopStateEvent('popstate')); }}
                    className="text-xs px-3 py-1 rounded-md bg-amber-500/20 text-amber-300 hover:bg-amber-500/30 font-medium transition-colors"
                    data-testid="demo-exit-btn"
                  >
                    Exit Demo
                  </button>
                </div>
              </div>
            )}
            <div className="h-16 flex items-center justify-between px-6">
            <div className="flex items-center gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search anything..."
                  className="w-80 h-10 pl-10 pr-4 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500/50 transition-colors"
                />
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* WebSocket Status */}
              <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg" title={wsConnected ? 'Real-time sync active' : 'Connecting...'}>
                <div className={`w-2 h-2 rounded-full ${wsConnected ? 'bg-emerald-400 animate-pulse' : 'bg-yellow-500'}`} />
                <span className="text-xs text-gray-500 font-medium" data-testid="ws-status">{wsConnected ? 'Live' : 'Sync...'}</span>
              </div>
              {/* Restart Tour */}
              <button
                onClick={() => window.__d3RestartTour?.()}
                className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/5 text-gray-500 border border-white/10 hover:bg-white/10 hover:text-gray-300 transition-all text-xs font-medium"
                title="Restart onboarding tour"
                data-testid="restart-tour-btn"
              >
                <HelpCircle className="w-3.5 h-3.5" />
                Tour
              </button>
              {/* Demo Mode Toggle */}
              <button
                onClick={toggleDemoMode}
                className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium transition-all border ${
                  demoMode
                    ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/20'
                    : 'bg-white/5 text-gray-500 border-white/10 hover:bg-white/10 hover:text-gray-300'
                }`}
                data-testid="demo-mode-toggle"
              >
                {demoMode ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}
                Demo {demoMode ? 'ON' : 'OFF'}
              </button>
              {demoMode && (
                <span className="px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-400 text-[10px] font-semibold tracking-wider uppercase border border-emerald-500/20 flex items-center gap-1.5" data-testid="demo-mode-badge">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                  Demo Mode Active
                </span>
              )}
              <button
                onClick={() => setDarkMode(!darkMode)}
                className="p-2.5 rounded-xl bg-white/5 hover:bg-white/10 transition-colors"
              >
                {darkMode ? (
                  <Sun className="w-5 h-5 text-gray-400" />
                ) : (
                  <Moon className="w-5 h-5 text-gray-400" />
                )}
              </button>
              <div className="relative">
                <button 
                  onClick={() => setNotificationsOpen(!notificationsOpen)}
                  className="relative p-2.5 rounded-xl bg-white/5 hover:bg-white/10 transition-colors"
                >
                  <Bell className="w-5 h-5 text-gray-400" />
                  {unreadNotifications > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center">
                      {unreadNotifications}
                    </span>
                  )}
                </button>
                <AnimatePresence>
                  <NotificationsDropdown 
                    isOpen={notificationsOpen} 
                    onClose={() => setNotificationsOpen(false)} 
                  />
                </AnimatePresence>
              </div>
            </div>
            </div>
          </header>

          {/* Screen Content */}
          <main className="flex-1 p-6 overflow-y-auto">
            <AnimatePresence mode="wait">
              <motion.div
                key={safeScreen}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
              >
                {renderScreen()}
              </motion.div>
            </AnimatePresence>
          </main>
        </div>

        {/* Click outside to close notifications */}
        {notificationsOpen && (
          <div 
            className="fixed inset-0 z-30" 
            onClick={() => setNotificationsOpen(false)}
          />
        )}

        {/* Onboarding Tour */}
        <OnboardingTour onNavigate={(id) => {
          if (visibleNavItems.some(n => n.id === id)) setActiveScreen(id);
        }} />
      </div>
    </div>
  );
};

export const AppDashboard = () => {
  return (
    <DataProvider>
      <DashboardContent />
    </DataProvider>
  );
};
